import { Helmet } from "react-helmet-async";
import { ArrowRight } from "lucide-react";
import { Button } from "../components/ui/Button";

const sec = "py-[clamp(44px,6vw,64px)]";
const email = "polina@transitionbridge.org";
const subject = "Evaluare%20energetic%C4%83%20preliminar%C4%83";

const situations = [
  ["01", "Energie solară existentă", "Există deja o instalație fotovoltaică, dar o parte din energia produsă nu poate fi utilizată eficient din cauza profilului de consum sau a limitărilor rețelei."],
  ["02", "Extinderea producției solare", "Întreprinderea intenționează să extindă producția solară și vrea să înțeleagă dacă este justificat un sistem de stocare a energiei."],
  ["03", "Răcire și sarcini sezoniere", "Instalațiile frigorifice, procesele de răcire sau vârfurile sezoniere generează consumuri și costuri ridicate."],
  ["04", "Pompare și irigare", "Stațiile de pompare și sistemele de irigare au un consum semnificativ de energie."],
  ["05", "Procese termice", "Producția depinde de gaze, abur sau alte procese termice și se analizează posibilitatea electrificării parțiale."],
  ["06", "Continuitate și capacitate", "Întreprinderea urmărește reducerea expunerii la creșterea prețurilor la energie, întreruperi sau limitări de capacitate."],
  ["07", "Modernizare și finanțare", "Este planificată o modernizare și trebuie verificat ce mecanisme de finanțare sau sprijin ar putea fi aplicabile."]
];

const chain = ["Problemă", "Date", "Evaluare", "Soluții", "Economie", "Finanțare", "Pasul următor"];

const analysisChain = ["aplicabilitate tehnică", "efect economic potențial", "date suplimentare", "opțiuni tehnologice", "mecanisme de finanțare"];

const outcomes = ["STOP", "ANALIZĂ SUPLIMENTARĂ", "INVESTIGAȚIE TEHNICĂ", "PREGĂTIREA UNUI PROIECT-PILOT"];

const deliverables = [
  "problema energetică reală",
  "opțiunile care merită analizate în continuare",
  "datele și verificările tehnice necesare",
  "logica economică a proiectului",
  "posibilitățile de finanțare care merită verificate",
  "următorul pas concret"
];

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export function RoVineEnergy() {
  return (
    <>
      <Helmet>
        <title>Evaluarea preliminară a rezilienței energetice a unei vinării | Transition Bridge</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="description" content="Program practic Transition Bridge pentru întreprinderile vitivinicole și de procesare din Republica Moldova." />
      </Helmet>

      {/* HERO */}
      <section className="bg-bg-primary pt-[calc(var(--header-height)+40px)] pb-[clamp(40px,5vw,64px)]">
        <div className="container-grid max-w-[860px]">
          <p className="eyebrow mb-4">EVALUARE ENERGETICĂ PRELIMINARĂ</p>
          <h1 className="text-balance text-[clamp(2rem,5vw,3.2rem)] font-extrabold leading-[1.05]">Evaluarea preliminară a rezilienței energetice a unei vinării</h1>
          <p className="mt-5 text-lg font-semibold leading-8 text-ink-primary md:text-xl">Program practic Transition Bridge pentru întreprinderile vitivinicole și de procesare din Republica Moldova.</p>
          <div className="mt-6 max-w-[760px] space-y-4 text-lg leading-8 text-ink-secondary">
            <p>Costul energiei, limitările rețelei, răcirea, pomparea apei, irigarea și procesele termice influențează tot mai mult costurile de producție și stabilitatea operațională a întreprinderilor.</p>
            <p>Nu fiecare companie are nevoie de o nouă instalație fotovoltaică, de un sistem de stocare sau de alte echipamente. Primul pas este să înțelegem unde apare problema reală, ce restricții există și dacă un proiect de modernizare are sens din punct de vedere tehnic și economic.</p>
          </div>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Button href={`mailto:${email}?subject=${subject}`}>Solicită o discuție preliminară</Button>
            <Button type="button" variant="secondary" onClick={() => scrollToId("proces")}>Vezi cum funcționează</Button>
          </div>
        </div>
      </section>

      {/* SECTION 2 — CE SITUAȚII PUTEM ANALIZA */}
      <section className={`bg-bg-secondary ${sec}`}>
        <div className="container-grid">
          <div className="max-w-[720px]">
            <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.4rem]">Ce situații putem analiza</h2>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {situations.map(([num, title, body]) => (
              <div key={num} className="rounded-2xl border border-line bg-surface p-6">
                <p className="mb-3 text-sm font-bold text-brand-blue/60">{num}</p>
                <h3 className="text-lg font-bold">{title}</h3>
                <p className="mt-3 text-base leading-7 text-ink-secondary">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — CUM DECURGE EVALUAREA */}
      <section id="proces" className={`scroll-mt-28 bg-bg-primary ${sec}`}>
        <div className="container-grid">
          <div className="max-w-[760px]">
            <p className="eyebrow mb-4">PROCESUL</p>
            <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.4rem]">Cum decurge evaluarea</h2>
          </div>

          {/* Native logic chain */}
          <div className="mt-8 flex flex-wrap items-center gap-y-3">
            {chain.map((item, i) => (
              <span key={item} className="flex items-center">
                <span className="rounded-full border border-line bg-surface px-3 py-1.5 text-sm font-semibold text-ink-secondary">{item}</span>
                {i < chain.length - 1 && <ArrowRight className="rtl-flip mx-2 h-4 w-4 text-brand-cyan" aria-hidden="true" />}
              </span>
            ))}
          </div>

          <ol className="mt-12 grid gap-10">
            <li className="grid gap-3 md:grid-cols-[200px_1fr] md:gap-10">
              <p className="text-sm font-bold text-brand-blue/60">01 · DISCUȚIE SCURTĂ</p>
              <div>
                <h3 className="text-xl font-bold">Discuție scurtă cu întreprinderea</h3>
                <p className="mt-3 max-w-2xl text-base leading-7 text-ink-secondary">Clarificăm problema de producție, obiectivele și principalele limitări.</p>
              </div>
            </li>

            <li className="grid gap-3 md:grid-cols-[200px_1fr] md:gap-10">
              <p className="text-sm font-bold text-brand-blue/60">02 · SET MINIM DE DATE</p>
              <div>
                <h3 className="text-xl font-bold">Set minim de date</h3>
                <p className="mt-3 max-w-2xl text-base leading-7 text-ink-secondary">Analizăm informațiile deja disponibile: consumul de energie, producția proprie, tarifele, programul de lucru, principalele sarcini și, dacă este cazul, capacitatea disponibilă a rețelei.</p>
                <p className="mt-4 max-w-2xl rounded-xl border-l-4 border-brand-blue bg-surface px-5 py-4 text-base leading-7 text-ink-secondary">Pentru prima etapă nu este necesar un audit energetic complet. Putem începe cu datele pe care întreprinderea le are deja.</p>
              </div>
            </li>

            <li className="grid gap-3 md:grid-cols-[200px_1fr] md:gap-10">
              <p className="text-sm font-bold text-brand-blue/60">03 · ANALIZĂ PRELIMINARĂ</p>
              <div>
                <h3 className="text-xl font-bold">Analiză preliminară</h3>
                <div className="mt-4 flex flex-wrap items-center gap-y-2">
                  {analysisChain.map((item, i) => (
                    <span key={item} className="flex items-center">
                      <span className="rounded-full border border-line bg-surface px-3 py-1.5 text-sm font-semibold text-ink-secondary">{item}</span>
                      {i < analysisChain.length - 1 && <ArrowRight className="rtl-flip mx-2 h-4 w-4 text-brand-cyan" aria-hidden="true" />}
                    </span>
                  ))}
                </div>
                <p className="mt-4 max-w-2xl text-base leading-7 text-ink-secondary">Transition Bridge, împreună cu specialiști relevanți, evaluează scenariile care merită analizate în continuare.</p>
              </div>
            </li>

            <li className="grid gap-3 md:grid-cols-[200px_1fr] md:gap-10">
              <p className="text-sm font-bold text-brand-blue/60">04 · DECIZIA</p>
              <div>
                <h3 className="text-xl font-bold">Decizia privind pasul următor</h3>
                <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                  {outcomes.map((item) => (
                    <li key={item} className="rounded-xl border border-line bg-surface px-4 py-3 text-center text-sm font-bold uppercase tracking-[0.08em] text-ink-primary">{item}</li>
                  ))}
                </ul>
              </div>
            </li>
          </ol>
        </div>
      </section>

      {/* SECTION 4 — CE PRIMEȘTE ÎNTREPRINDEREA */}
      <section className={`bg-bg-secondary ${sec}`}>
        <div className="container-grid">
          <div className="max-w-[720px]">
            <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.4rem]">Ce primește întreprinderea</h2>
          </div>
          <ul className="mt-10 grid max-w-[860px] gap-3">
            {deliverables.map((item) => (
              <li key={item} className="flex items-start gap-3 text-base leading-7 text-ink-secondary">
                <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-cyan" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
          <p className="mt-10 max-w-[860px] border-l-4 border-brand-blue ps-6 text-lg font-semibold leading-relaxed text-ink-primary">Transition Bridge nu este legat de un singur producător de echipamente. Soluțiile sunt selectate pornind de la nevoia reală a întreprinderii.</p>
        </div>
      </section>

      {/* SECTION 5 — CUM ÎNCEPEM */}
      <section className="petrol-section">
        <div className="container-grid max-w-[860px] py-[clamp(48px,6vw,72px)]">
          <p className="eyebrow-sage mb-4">PRIMUL PAS</p>
          <h2 className="text-balance text-3xl font-bold leading-[1.06] text-brand-darkText md:text-[2.4rem]">Primul pas este simplu</h2>
          <p className="mt-5 max-w-[720px] text-lg leading-8 text-brand-darkMuted">Pentru prima discuție sunt suficiente 20–30 de minute și o scurtă descriere a situației actuale.</p>
          <div className="mt-8">
            <Button href={`mailto:${email}`}>Scrie-i Polinei Zisman</Button>
          </div>
          <div className="mt-8 border-t border-white/10 pt-6 text-brand-darkText">
            <p className="text-lg font-bold">Polina Zisman</p>
            <p className="text-sm text-brand-darkMuted">Co-Founder · Moldova–Israel Partnerships</p>
            <p className="mt-1 text-sm text-brand-darkMuted">Transition Bridge</p>
            <p className="mt-3 text-sm">
              <a className="text-brand-cyan underline underline-offset-2" href={`mailto:${email}`}>{email}</a>
            </p>
            <p className="mt-1 text-sm">
              <a className="text-brand-cyan underline underline-offset-2" href="https://transitionbridge.org" target="_blank" rel="noopener noreferrer">transitionbridge.org</a>
            </p>
          </div>
          <p className="mt-8 max-w-[720px] text-sm leading-6 text-brand-darkMuted">Transition Bridge conectează nevoile reale ale companiilor din Moldova cu expertiză și tehnologii relevante din Israel și din alte piețe.</p>
        </div>
      </section>
    </>
  );
}
